import React, { useState, useEffect, useCallback } from 'react';
import { getSupabaseClient } from "../lib/supabase";
import { useGuideTimezone } from '../hooks/useGuideTimezone';
import { formatTimeInTimezone, isToday, isTomorrow, getTimezoneAbbreviation } from '../utils/timezoneUtils';
import {
  ExclamationTriangleIcon,
  XMarkIcon,
  CalendarIcon,
  CreditCardIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

interface Client {
  id: string;
  name: string;
  avatar: string;
  specialization: string;
  progress: number;
  totalSessions: number;
  nextSession?: string;
  lastSession?: string;
  isActive: boolean;
  unreadCount?: number;
  seekerId?: string;
}

interface UpcomingSession {
  id: string;
  clientName: string;
  sessionType: string;
  date: string;
  time: string;
}

interface Alert {
  seekerName: string;
  seekerId: string;
  details: string;
}

export default function HubPage({ onViewDetails, onOpenChat }: { onViewDetails?: (seeker?: any) => void; onOpenChat?: (seekerId: string) => void }) {
  const [userProfile, setUserProfile] = useState<{ name?: string }>({});
  const [clients, setClients] = useState<Client[]>([]);
  const [upcomingSessions, setUpcomingSessions] = useState<UpcomingSession[]>([]);
  const [awaitingPackageAlerts, setAwaitingPackageAlerts] = useState<Alert[]>([]);
  const [packageEndingAlerts, setPackageEndingAlerts] = useState<Alert[]>([]);
  const [unscheduledSessionsAlerts, setUnscheduledSessionsAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [calendarConnected, setCalendarConnected] = useState<boolean | null>(null);
  const [calendarAlertDismissed, setCalendarAlertDismissed] = useState(false);
  const [stripeConnected, setStripeConnected] = useState<boolean | null>(null);
  const [stripeAlertDismissed, setStripeAlertDismissed] = useState(false);

  const supabase = getSupabaseClient();
  const { timezone: guideTimezone } = useGuideTimezone();

  // Helper function to get initials from a name
  const getInitials = (name: string): string => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  // Check if user has connected a calendar
  const checkCalendarConnection = useCallback(async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        return;
      }

      // Check Google Calendar
      const { data: googleData } = await supabase
        .from('guide_calendars')
        .select('id')
        .eq('user_id', session.user.id)
        .eq('provider', 'google')
        .limit(1)
        .maybeSingle();

      if (googleData) {
        setCalendarConnected(true);
        return;
      }

      // Check Microsoft Outlook
      const { data: msData } = await supabase
        .from('user_identities')
        .select('id')
        .eq('user_id', session.user.id)
        .eq('provider', 'microsoft')
        .limit(1)
        .maybeSingle();

      if (msData) {
        setCalendarConnected(true);
        return;
      }

      setCalendarConnected(false);
    } catch (error) {
      console.error('Error checking calendar connection:', error);
    }
  }, [supabase]);

  // Check if user has connected Stripe
  const checkStripeConnection = useCallback(async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        return;
      }

      const env = typeof window !== 'undefined' ? (window as any).__ENV__ : undefined;
      const baseUrl = env?.SUPABASE_URL;
      if (!baseUrl) {
        return;
      }

      const response = await fetch(`${baseUrl}/functions/v1/connect-status`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        setStripeConnected(false);
        return;
      }

      const data = await response.json();
      setStripeConnected(data.connected && data.status?.fully_setup);
    } catch (err) {
      console.error('Error checking Stripe connection:', err);
    }
  }, [supabase]);

  useEffect(() => {
    loadData();
    checkCalendarConnection();
    checkStripeConnection();
  }, [checkCalendarConnection, checkStripeConnection]);

  const loadData = async () => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch profile data
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', user.id)
        .single();

      if (profile) {
        setUserProfile({ name: profile.full_name?.split(' ')[0] || 'Guide' });
      }

      const allowedSeekerIds = new Set<string>();
      const userIdToSeekerId = new Map<string, string>(); // Map auth user_id to matches seeker_id
      let shouldFilterByMatches = false;

      try {
        const { data: matchRows, error: matchesError } = await supabase
          .from('matches')
          .select('seeker_id, status')
          .eq('guide_id', user.id);

        if (matchesError) {
          console.warn('Unable to load match statuses for hub view', matchesError);
        } else if (matchRows && matchRows.length > 0) {
          shouldFilterByMatches = true;
          const activeRecordIds: string[] = [];
          matchRows.forEach(row => {
            const rawId = typeof row.seeker_id === 'string'
              ? row.seeker_id
              : row.seeker_id
              ? String(row.seeker_id)
              : null;
            if (rawId && row.status === 'active') {
              allowedSeekerIds.add(rawId);
              activeRecordIds.push(rawId);
            }
          });

          if (activeRecordIds.length > 0) {
            const { data: mappingRows, error: mappingError } = await supabase
              .from('seekers')
              .select('seeker_id, user_id')
              .in('seeker_id', activeRecordIds);

            if (mappingError) {
              console.warn('Unable to map seeker IDs for hub view', mappingError);
            } else if (mappingRows) {
              mappingRows.forEach(row => {
                const userId = row?.user_id ? String(row.user_id) : null;
                const seekerId = row?.seeker_id ? String(row.seeker_id) : null;
                if (userId) {
                  allowedSeekerIds.add(userId);
                  if (seekerId) {
                    userIdToSeekerId.set(userId, seekerId);
                  }
                }
              });
            }
          }
        }
      } catch (matchErr) {
        console.warn('Unexpected error loading active matches for hub view', matchErr);
      }

      // Fetch active clients (seekers with active package offers)
      const { data: activeOffers, error: offersError } = await supabase
        .from('package_offers')
        .select(`
          id,
          seeker_id,
          name,
          status,
          package_offer_units (
            id,
            status
          )
        `)
        .eq('guide_id', user.id)
        .in('status', ['sent', 'accepted'])
        .order('created_at', { ascending: false });

      if (offersError) {
        console.error('Error fetching package offers:', offersError);
      }

      // Get unique seeker IDs and build client list
      const clientsData: Client[] = [];
      const seenSeekerIds = new Set<string>();

      for (const offer of activeOffers || []) {
        if (!offer.seeker_id || seenSeekerIds.has(offer.seeker_id)) continue;
        if (shouldFilterByMatches && !allowedSeekerIds.has(offer.seeker_id)) continue;
        seenSeekerIds.add(offer.seeker_id);

        // Get seeker profile (email/phone intentionally not fetched to protect privacy)
        const { data: seekerProfile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', offer.seeker_id)
          .single();

        // Calculate progress from units
        const units = offer.package_offer_units || [];
        const totalSessions = units.length;
        const completedSessions = units.filter((unit: any) => unit.status === 'fulfilled').length;

        // Get next or last session - use start_time for ordering and filtering
        const { data: nextSession } = await supabase
          .from('scheduled_sessions')
          .select('start_time, guide_timezone')
          .eq('guide_id', user.id)
          .eq('seeker_id', offer.seeker_id)
          .in('session_status', ['scheduled', 'confirmed'])
          .gte('start_time', new Date().toISOString())
          .order('start_time', { ascending: true })
          .limit(1)
          .maybeSingle();

        const { data: lastSession } = await supabase
          .from('scheduled_sessions')
          .select('start_time, guide_timezone')
          .eq('guide_id', user.id)
          .eq('seeker_id', offer.seeker_id)
          .eq('session_status', 'completed')
          .order('start_time', { ascending: false })
          .limit(1)
          .maybeSingle();

        const formatSessionTime = (session: { start_time: string; guide_timezone?: string }) => {
          try {
            // Use start_time (UTC) as source of truth
            const utcDate = new Date(session.start_time);

            const tz = session.guide_timezone || guideTimezone;
            const todayCheck = isToday(utcDate.toISOString(), tz);
            const tomorrowCheck = isTomorrow(utcDate.toISOString(), tz);

            const timeStr = formatTimeInTimezone(utcDate.toISOString(), tz);
            const tzAbbrev = getTimezoneAbbreviation(utcDate.toISOString(), tz);
            const timePart = tzAbbrev ? `${timeStr} ${tzAbbrev}` : timeStr;

            if (todayCheck) {
              return `Today ${timePart}`;
            } else if (tomorrowCheck) {
              return `Tomorrow ${timePart}`;
            } else {
              // Format as "Jan 6, 3:00 PM EST"
              const dateStr = new Intl.DateTimeFormat('en-US', {
                timeZone: tz,
                month: 'short',
                day: 'numeric'
              }).format(utcDate);
              return `${dateStr}, ${timePart}`;
            }
          } catch (error) {
            console.error('Error formatting session time:', error);
            return 'Invalid date';
          }
        };

        // GCS-186: Compare calendar dates in the guide's timezone, not raw UTC milliseconds.
        // The old Math.ceil approach returned "Yesterday" for sessions earlier the same day.
        const formatLastSessionTime = (startTimeUtc: string, timezone?: string) => {
          try {
            const sessionDate = new Date(startTimeUtc);
            const tz = timezone || guideTimezone;

            // Get calendar date strings in the guide's timezone for accurate day comparison
            const sessionDay = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(sessionDate);
            const todayDay = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(new Date());

            if (sessionDay === todayDay) return 'Today';

            // Calculate day difference using date-only strings to avoid timezone/midnight issues
            const sessionMs = new Date(sessionDay + 'T00:00:00').getTime();
            const todayMs = new Date(todayDay + 'T00:00:00').getTime();
            const diffDays = Math.round((todayMs - sessionMs) / (1000 * 60 * 60 * 24));

            if (diffDays === 1) return 'Yesterday';
            if (diffDays < 7) return `${diffDays} days ago`;
            if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
            return new Intl.DateTimeFormat('en-US', {
              timeZone: tz,
              month: 'short',
              day: 'numeric'
            }).format(sessionDate);
          } catch (error) {
            console.error('Error formatting last session time:', error);
            return 'Invalid date';
          }
        };

        const seekerName = seekerProfile?.full_name || 'Seeker';
        clientsData.push({
          id: offer.seeker_id,
          name: seekerName,
          avatar: getInitials(seekerName),
          specialization: offer.name || 'Package',
          progress: completedSessions,
          totalSessions: totalSessions || 0,
          nextSession: nextSession ? formatSessionTime(nextSession) : undefined,
          lastSession: lastSession ? formatLastSessionTime(lastSession.start_time, lastSession.guide_timezone) : undefined,
          isActive: offer.status === 'accepted',
          seekerId: userIdToSeekerId.get(offer.seeker_id) || undefined,
        });
      }

      // Fetch unread message counts for all clients
      if (clientsData.length > 0) {
        const { data: notifications, error: notifError } = await supabase
          .from('message_notifications')
          .select('sender, count');

        if (!notifError && notifications) {
          const notificationMap = new Map(
            notifications.map(n => [n.sender, n.count])
          );

          clientsData.forEach(client => {
            // message_notifications uses auth user_id as sender
            client.unreadCount = notificationMap.get(client.id) || 0;
          });
        }
      }

      setClients(clientsData);

      // Fetch upcoming sessions (next 7 days) - use start_time for filtering and ordering
      const today = new Date();
      const nextWeek = new Date(today);
      nextWeek.setDate(nextWeek.getDate() + 7);

      const { data: sessions, error: sessionsError } = await supabase
        .from('scheduled_sessions')
        .select(`
          id,
          session_type,
          start_time,
          end_time,
          guide_timezone,
          seeker_id
        `)
        .eq('guide_id', user.id)
        .in('session_status', ['scheduled', 'confirmed'])
        .gte('start_time', today.toISOString())
        .lte('start_time', nextWeek.toISOString())
        .order('start_time', { ascending: true });

      if (sessionsError) {
        console.error('Error fetching sessions:', sessionsError);
      }

      // Build upcoming sessions list
      const upcomingSessionsData: UpcomingSession[] = [];

      for (const session of sessions || []) {
        if (shouldFilterByMatches && !allowedSeekerIds.has(session.seeker_id)) {
          continue;
        }
        // Skip sessions without start_time
        if (!session.start_time) {
          console.warn('Session missing start_time, skipping:', session.id);
          continue;
        }

        // Get seeker name
        const { data: seekerProfile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', session.seeker_id)
          .single();

        // Use start_time (UTC) as source of truth
        const utcStartDate = new Date(session.start_time);

        const tz = session.guide_timezone || guideTimezone;
        const todayCheck = isToday(utcStartDate.toISOString(), tz);
        const tomorrowCheck = isTomorrow(utcStartDate.toISOString(), tz);

        let dateLabel = '';
        if (todayCheck) {
          dateLabel = 'Today';
        } else if (tomorrowCheck) {
          dateLabel = 'Tomorrow';
        } else {
          dateLabel = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            weekday: 'long',
            month: 'short',
            day: 'numeric'
          }).format(utcStartDate);
        }

        const timeLabel = formatTimeInTimezone(utcStartDate.toISOString(), tz);

        // Calculate end time
        let endTimeLabel: string;
        if (session.end_time) {
          endTimeLabel = formatTimeInTimezone(session.end_time, tz);
        } else {
          // Assume 1 hour if no end_time
          const endDate = new Date(utcStartDate.getTime() + 60 * 60 * 1000);
          endTimeLabel = formatTimeInTimezone(endDate.toISOString(), tz);
        }

        const sessionTypeMap: Record<string, string> = {
          consultation: 'Consultation',
          preparation: 'Preparation Session',
          integration: 'Integration Session',
          journey: 'Journey Session',
          follow_up: 'Follow-up Session'
        };

        upcomingSessionsData.push({
          id: session.id,
          clientName: seekerProfile?.full_name || 'Seeker',
          sessionType: sessionTypeMap[session.session_type] || session.session_type,
          date: dateLabel,
          time: `${timeLabel} - ${endTimeLabel}`
        });
      }

      setUpcomingSessions(upcomingSessionsData);

      // Fetch alert data
      // 1. Seekers awaiting package (completed consultation but no package sent)
      const { data: completedConsultations } = await supabase
        .from('scheduled_sessions')
        .select('seeker_id, start_time')
        .eq('guide_id', user.id)
        .eq('session_type', 'consultation')
        .eq('session_status', 'completed');

      const awaitingPackage: Alert[] = [];
      const seekersWithConsultation = new Set(
        (completedConsultations || [])
          .map(s => s.seeker_id)
          .filter(seekerId => !shouldFilterByMatches || allowedSeekerIds.has(seekerId))
      );

      for (const seekerId of seekersWithConsultation) {
        // Check if they have any package offers
        const { data: packageCheck } = await supabase
          .from('package_offers')
          .select('id')
          .eq('guide_id', user.id)
          .eq('seeker_id', seekerId)
          .limit(1)
          .maybeSingle();

        if (!packageCheck) {
          // No package sent yet
          const { data: seekerProfile } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', seekerId)
            .single();

          const consultationStartTime = completedConsultations?.find(s => s.seeker_id === seekerId)?.start_time;
          const formattedDate = consultationStartTime
            ? new Intl.DateTimeFormat('en-US', {
                timeZone: guideTimezone,
                month: 'short',
                day: 'numeric'
              }).format(new Date(consultationStartTime))
            : '';

          awaitingPackage.push({
            seekerName: seekerProfile?.full_name || 'Seeker',
            seekerId,
            details: formattedDate ? `Consultation completed ${formattedDate}` : 'Consultation completed',
          });
        }
      }

      setAwaitingPackageAlerts(awaitingPackage);

      // 2. Package ending (less than 2 sessions unscheduled/in the future)
      const packageEnding: Alert[] = [];

      for (const offer of activeOffers || []) {
        if (!offer.seeker_id) continue;
        if (shouldFilterByMatches && !allowedSeekerIds.has(offer.seeker_id)) continue;

        const units = offer.package_offer_units || [];
        const unscheduledUnits = units.filter((unit: any) =>
          unit.status === 'unallocated' || unit.status === 'cancelled'
        );

        // Also check for scheduled sessions in the future - use start_time
        const { data: futureSessions } = await supabase
          .from('scheduled_sessions')
          .select('id')
          .eq('guide_id', user.id)
          .eq('seeker_id', offer.seeker_id)
          .in('session_status', ['scheduled', 'confirmed'])
          .gte('start_time', new Date().toISOString());

        const futureSessionCount = futureSessions?.length || 0;
        const totalAvailable = unscheduledUnits.length + futureSessionCount;

        if (totalAvailable < 2 && totalAvailable > 0) {
          const { data: seekerProfile } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', offer.seeker_id)
            .single();

          packageEnding.push({
            seekerName: seekerProfile?.full_name || 'Seeker',
            seekerId: offer.seeker_id,
            details: `Only ${totalAvailable} session${totalAvailable === 1 ? '' : 's'} remaining`,
          });
        }
      }

      setPackageEndingAlerts(packageEnding);

      // 3. Unscheduled purchased sessions
      const unscheduledSessions: Alert[] = [];

      for (const offer of activeOffers || []) {
        if (!offer.seeker_id || offer.status !== 'accepted') continue;
        if (shouldFilterByMatches && !allowedSeekerIds.has(offer.seeker_id)) continue;

        const units = offer.package_offer_units || [];
        const unscheduledUnits = units.filter((unit: any) => unit.status === 'unallocated');

        if (unscheduledUnits.length > 0) {
          const { data: seekerProfile } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', offer.seeker_id)
            .single();

          unscheduledSessions.push({
            seekerName: seekerProfile?.full_name || 'Seeker',
            seekerId: offer.seeker_id,
            details: `${unscheduledUnits.length} session${unscheduledUnits.length === 1 ? '' : 's'} need scheduling`,
          });
        }
      }

      setUnscheduledSessionsAlerts(unscheduledSessions);

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-[#E8DFBD] rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="h-48 bg-[#E8DFBD] rounded"></div>
            <div className="h-48 bg-[#E8DFBD] rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  return (
    <div className="p-6 bg-[#FFFCEF] text-[#2C2731] min-h-full">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-4xl font-medium text-[#2C2731] mb-2 font-recoleta">
          {getGreeting()}, {userProfile.name || 'Guide'}
        </h1>
        {(awaitingPackageAlerts.length > 0 || packageEndingAlerts.length > 0 || unscheduledSessionsAlerts.length > 0) && (
          <p className="text-[#2C2731]/80">The below need your attention:</p>
        )}
      </div>

      {/* Setup Warnings */}
      {calendarConnected === false && !calendarAlertDismissed && (
        <div className="mb-6 rounded-lg border border-amber-500 bg-amber-50 p-4">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-amber-800">
                No calendar connected
              </h3>
              <p className="mt-1 text-sm text-amber-700">
                Connect your Google Calendar or Outlook Calendar to sync your availability and avoid double-bookings.
              </p>
              <div className="mt-3">
                <button
                  onClick={() => {
                    window.location.hash = 'settings-calendar';
                  }}
                  className="inline-flex items-center rounded-md bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700 transition-colors"
                >
                  <CalendarIcon className="h-4 w-4 mr-1.5" />
                  Connect Calendar
                </button>
              </div>
            </div>
            <button
              onClick={() => setCalendarAlertDismissed(true)}
              className="ml-4 flex-shrink-0 rounded p-1 text-amber-500 hover:bg-amber-100 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {stripeConnected === false && !stripeAlertDismissed && (
        <div className="mb-6 rounded-lg border border-amber-500 bg-amber-50 p-4">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-amber-800">
                Stripe not connected
              </h3>
              <p className="mt-1 text-sm text-amber-700">
                Connect your Stripe account to receive payments from seekers. You won't be able to send package offers until Stripe is set up.
              </p>
              <div className="mt-3">
                <button
                  onClick={() => {
                    window.location.hash = 'settings-payments';
                  }}
                  className="inline-flex items-center rounded-md bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700 transition-colors"
                >
                  <CreditCardIcon className="h-4 w-4 mr-1.5" />
                  Set Up Payments
                </button>
              </div>
            </div>
            <button
              onClick={() => setStripeAlertDismissed(true)}
              className="ml-4 flex-shrink-0 rounded p-1 text-amber-500 hover:bg-amber-100 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Alert Cards */}
      {(awaitingPackageAlerts.length > 0 || packageEndingAlerts.length > 0 || unscheduledSessionsAlerts.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Seeker Awaiting Package */}
          {awaitingPackageAlerts.length > 0 && (
            <div className="bg-[#2C2731] border border-[#FFFCEF] rounded-lg p-6 text-[#FFFCEF]">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-[#FFFCEF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold mb-2">
                    Awaiting Package ({awaitingPackageAlerts.length})
                  </h3>
                  <div className="space-y-2">
                    {awaitingPackageAlerts.slice(0, 3).map((alert) => (
                      <div key={alert.seekerId} className="flex items-center justify-between text-sm">
                        <button
                          onClick={() =>
                            onViewDetails &&
                            onViewDetails({ id: alert.seekerId, name: alert.seekerName })
                          }
                          className="text-[#FFFCEF] hover:underline font-medium text-left ph-mask"
                        >
                          {alert.seekerName}
                        </button>
                        <span className="text-xs text-[#FFFCEF]/80">{alert.details}</span>
                      </div>
                    ))}
                    {awaitingPackageAlerts.length > 3 && (
                      <p className="text-xs text-[#FFFCEF]/80 mt-2">+{awaitingPackageAlerts.length - 3} more</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Package Ending */}
          {packageEndingAlerts.length > 0 && (
            <div className="bg-[#2C2731] border border-[#FFFCEF] rounded-lg p-6 text-[#FFFCEF]">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-[#FFFCEF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold mb-2">
                    Package Ending ({packageEndingAlerts.length})
                  </h3>
                  <div className="space-y-2">
                    {packageEndingAlerts.slice(0, 3).map((alert) => (
                      <div key={alert.seekerId} className="flex items-center justify-between text-sm">
                        <button
                          onClick={() =>
                            onViewDetails &&
                            onViewDetails({ id: alert.seekerId, name: alert.seekerName })
                          }
                          className="text-[#FFFCEF] hover:underline font-medium text-left ph-mask"
                        >
                          {alert.seekerName}
                        </button>
                        <span className="text-xs text-[#FFFCEF]/80">{alert.details}</span>
                      </div>
                    ))}
                    {packageEndingAlerts.length > 3 && (
                      <p className="text-xs text-[#FFFCEF]/80 mt-2">+{packageEndingAlerts.length - 3} more</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Unscheduled Sessions */}
          {unscheduledSessionsAlerts.length > 0 && (
            <div className={`bg-[#2C2731] border border-[#FFFCEF] rounded-lg p-6 text-[#FFFCEF] ${awaitingPackageAlerts.length === 0 || packageEndingAlerts.length === 0 ? 'md:col-span-2' : ''}`}>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-[#FFFCEF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold mb-2">
                    Unscheduled Sessions ({unscheduledSessionsAlerts.length})
                  </h3>
                  <div className="space-y-2">
                    {unscheduledSessionsAlerts.slice(0, 5).map((alert) => (
                      <div key={alert.seekerId} className="flex items-center justify-between text-sm">
                        <button
                          onClick={() =>
                            onViewDetails &&
                            onViewDetails({ id: alert.seekerId, name: alert.seekerName })
                          }
                          className="text-[#FFFCEF] hover:underline font-medium text-left ph-mask"
                        >
                          {alert.seekerName}
                        </button>
                        <span className="text-xs text-[#FFFCEF]/80">{alert.details}</span>
                      </div>
                    ))}
                    {unscheduledSessionsAlerts.length > 5 && (
                      <p className="text-xs text-[#FFFCEF]/80 mt-2">+{unscheduledSessionsAlerts.length - 5} more</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        {/* Active Clients */}
        <div className="xl:col-span-2">
          <h2 className="text-lg font-medium text-[#2C2731] mb-4 font-recoleta">My active clients</h2>
          {clients.length === 0 ? (
            <div className="bg-[#FFFCEF] border border-[#2C2731] rounded-lg p-12 text-center text-[#2C2731]">
              <div className="text-[#2C2731]/60 mb-2">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-base font-medium mb-1">No active clients yet</h3>
              <p className="text-sm text-[#2C2731]/80">Clients with active packages will appear here</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {clients.map((client) => (
                <div key={client.id} className="bg-[#FFFCEF] border border-[#2C2731] rounded-lg p-6 text-[#2C2731]">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-[#E8DFBD] rounded-full flex items-center justify-center text-lg font-semibold text-[#2C2731]">
                        {client.avatar}
                      </div>
                      <div>
                        <h3 className="font-medium">{client.name}</h3>
                        <p className="text-sm text-[#2C2731]/80">{client.specialization}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className={`w-2 h-2 rounded-full ${client.isActive ? 'bg-[#2C2731]' : 'bg-[#E8DFBD]'}`}></div>
                      <span className="text-sm">Active</span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span>Sessions Completed</span>
                      <span>{client.progress} / {client.totalSessions}</span>
                    </div>
                    <div className="w-full bg-[#E8DFBD] rounded-full h-2">
                      <div
                        className="bg-[#2C2731] h-2 rounded-full transition-all duration-300"
                        style={{ width: `${client.totalSessions > 0 ? (client.progress / client.totalSessions) * 100 : 0}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Session Info */}
                  <div className="flex justify-between items-center text-sm">
                    <div>
                      {client.nextSession ? (
                        <span>Next Session: {client.nextSession}</span>
                      ) : client.lastSession ? (
                        <span>Last Session: {client.lastSession}</span>
                      ) : (
                        <span className="text-[#2C2731]/50">No sessions scheduled</span>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3 mt-4">
                    <button
                      onClick={() => client.seekerId && onOpenChat && onOpenChat(client.seekerId)}
                      disabled={!client.seekerId}
                      className="relative inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[#2C2731] rounded-md bg-[#FFFCEF] text-[#2C2731] hover:bg-[#F3EBCD] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChatBubbleLeftRightIcon className="h-4 w-4" />
                      Message
                      {client.unreadCount && client.unreadCount > 0 ? (
                        <span className="absolute -top-2 -right-2 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold text-[#FFFCEF] bg-[#2C2731] rounded-full min-w-[18px]">
                          {client.unreadCount}
                        </span>
                      ) : null}
                    </button>
                    <button
                      onClick={() => onViewDetails && onViewDetails(client)}
                      className="px-3 py-1.5 text-sm border border-[#2C2731] bg-[#2C2731] text-[#FFFCEF] rounded-md hover:bg-[#241F2A] transition-colors"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming Sessions Sidebar */}
        <div>
          <h2 className="text-lg font-medium text-[#2C2731] mb-4 font-recoleta">Upcoming Sessions</h2>
          {upcomingSessions.length === 0 ? (
            <div className="bg-[#FFFCEF] border border-[#2C2731] rounded-lg p-8 text-center text-[#2C2731]">
              <div className="text-[#2C2731]/60 mb-2">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-sm font-medium mb-1">No upcoming sessions</h3>
              <p className="text-xs text-[#2C2731]/80">Sessions in the next 7 days will appear here</p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {upcomingSessions.map((session) => (
                  <div key={session.id} className="bg-[#FFFCEF] border border-[#2C2731] rounded-lg p-4 text-[#2C2731]">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-[#E8DFBD] rounded-lg flex items-center justify-center text-[#2C2731]">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm">{session.clientName}</h4>
                        <p className="text-xs text-[#2C2731]/80 mt-0.5">{session.sessionType}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs font-medium">{session.date}</span>
                          <span className="text-xs text-[#2C2731]/80">{session.time}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* View Full Schedule Button */}
              <button className="w-full mt-4 px-4 py-2 border border-[#2C2731] rounded-md text-sm font-medium bg-[#FFFCEF] text-[#2C2731] hover:bg-[#F3EBCD] transition-colors flex items-center justify-center space-x-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>View Full Schedule</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
